import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/yoga-instructor-real.jpg";

export const yogaInstructors: Persona = {
  slug: "yoga-pilates-instructors",
  profession: { hu: "jógaoktató", en: "yoga instructor" },
  professionPlural: { hu: "jógaoktatók", en: "yoga instructors" },
  professionGenitive: { hu: "jógaoktatóként", en: "as a yoga instructor" },
  heroH1: {
    hu: "Több vendég, kevesebb adminisztráció. Ma este élő oldal.",
    en: "More students, less admin. Live website tonight.",
  },
  heroSub: {
    hu: "Teljes körű AI asszisztens jógaoktatóknak és stúdióknak: brand és weboldal, óra-foglalás és bérletek, Instagram-tartalom, e-mail és WhatsApp automatizáció, CRM és bevétel-analitika — egy helyen.",
    en: "A full AI assistant for yoga instructors and studios: brand and website, class bookings and passes, Instagram content, email and WhatsApp automations, CRM and revenue analytics — all in one place.",
  },
  heroImage,
  heroImageAlt: "Yoga instructor teaching in studio",
  chatExamplePrompt: {
    hu: "Töröld a holnap reggeli vinyasát, beteg vagyok",
    en: "Cancel tomorrow morning's vinyasa, I'm sick",
  },
  chatExampleResponse: {
    hu: "Töröltem és értesítettem a 12 résztvevőt. Felajánlottam a szerdai órát csere gyanánt.",
    en: "Cancelled and notified all 12 attendees. Offered Wednesday's class as a replacement.",
  },
  accentColor: "purple",

  defaultMode: "solo",
  teamSize: { min: 1, max: 15 },
  soloLabel: { hu: "Szóló jógaoktató", en: "Solo yoga instructor" },
  teamLabel: {
    hu: "Stúdió több oktatóval",
    en: "Studio with multiple instructors",
  },

  soloDayInLife: [
    {
      time: "06:30",
      what: {
        hu: "Felkelsz. Az AI értesít e-mailben: 8 vendég regisztrált éjszaka a reggeli órára.",
        en: "You wake up. AI emails you: 8 students registered overnight for the morning class.",
      },
    },
    {
      time: "08:00",
      what: {
        hu: "Megtartod az órát. Egy vendég új, jön először: a Bespire-ben már van profilja, látod az emailcímét és telefonszámát.",
        en: "You teach the class. One student is new: they already have a profile in Bespire, you see their email and phone.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "Két vendégnek lejár a bérlete. Az AI emlékeztetőt küld nekik este 6-kor.",
        en: "Two students' passes are expiring. AI sends them a reminder at 6 PM.",
      },
    },
    {
      time: "14:00",
      what: {
        hu: "Egy vendég lemond egy esti órát. Az AI 60 másodperc múlva értesíti a várólista első emberét.",
        en: "A student cancels an evening class. AI notifies the first person on the waitlist within 60 seconds.",
      },
    },
    {
      time: "17:00",
      what: {
        hu: "Az AI elkészít egy heti Instagram posztot a péntek esti hold-órádra. Jóváhagyod.",
        en: "AI drafts a weekly Instagram post for your Friday evening moon class. You approve it.",
      },
    },
    {
      time: "21:00",
      what: {
        hu: "Megnézed a dashboardot: 14 új foglalás, 3 új tag, 78 000 Ft bevétel.",
        en: "You check the dashboard: 14 new bookings, 3 new members, 78,000 HUF revenue.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "07:00",
      what: {
        hu: "A stúdió dashboardja: 4 oktatód 14 órát ad ma. Két óra fedés volt: az AI áthelyezte a kisebb csoportot a B teremre.",
        en: "Studio dashboard: your 4 instructors teach 14 classes today. Two overlapped: AI moved the smaller group to studio B.",
      },
    },
    {
      time: "09:00",
      what: {
        hu: "Eszti oktató beteg. Egy chat üzenet, és az AI értesíti a 22 érintett vendéget, felajánlja a délutáni Pilates-t.",
        en: "Instructor Eszti is sick. One chat message and AI notifies all 22 affected students, offers the afternoon Pilates.",
      },
    },
    {
      time: "12:00",
      what: {
        hu: "Új oktató, Bence érkezik. Felveszed a Bespire-be, 5 percen belül publikálódik a profilja a stúdió oldalán.",
        en: "New instructor Bence joins. You add him to Bespire, his profile is live on the studio site within 5 minutes.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Az AI riportot küld: ezen a héten az 5 órák voltak a legnépszerűbbek. Javasolja az ár-emelést a péntek esti slot-okra.",
        en: "AI report: 5 PM classes were most popular this week. Suggests a price increase for Friday evening slots.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Hétvégi workshop telik: az AI várólistára teszi a túljelentkezőket és ajánl egy 2. csoportot.",
        en: "Weekend workshop fills up: AI adds overflow to waitlist and suggests opening a 2nd group.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Bevétel-statisztika oktatónként, automatikus elszámolás Számlázz.hu-val. A bér-számítás már kész.",
        en: "Revenue stats per instructor, automatic invoicing via Számlázz.hu. Payroll is ready.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: { hu: "Minden egy helyen", en: "Everything in one place" },
      body: {
        hu: "Egy hely: naptár, számla, vendég, marketing. Nem kell 5 különálló eszközt nyitogatni.",
        en: "One place: calendar, invoicing, clients, marketing. No more juggling 5 separate tools.",
      },
    },
    {
      icon: "Bot",
      title: { hu: "Az AI a helyettesem", en: "AI is my assistant" },
      body: {
        hu: "Foglalások, lemondások, emlékeztetők: az AI mindent helyetted intéz.",
        en: "Bookings, cancellations, reminders: AI handles it all for you.",
      },
    },
    {
      icon: "MessageCircle",
      title: { hu: "Nem kell technikai segítség", en: "No tech skills needed" },
      body: {
        hu: "Ha valamit változtatnál (új óra, ár-emelés, akció), chatben elmondod az AI-nak.",
        en: "Want to change something (new class, price increase, promo)? Just tell the AI in chat.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with automatic invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan oktató, egy ár",
        en: "Unlimited instructors, one price",
      },
      body: {
        hu: "Vegyél fel 2-t, 5-öt, 15-öt: ugyanaz a 10 000 Ft + ÁFA / hó. Mindenki saját naptárral és ügyfélbázissal.",
        en: "Add 2, 5, or 15: same 10,000 HUF + VAT / month. Each with their own calendar and client base.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Jogosultság-kezelés", en: "Role-based access" },
      body: {
        hu: "Tulajdonos, manager, oktató: mindenki csak azt látja, amihez joga van.",
        en: "Owner, manager, instructor: everyone sees only what they're allowed to.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI elosztja az órákat", en: "AI assigns classes" },
      body: {
        hu: "Ütközés? Helyettesítés? Az AI automatikusan újraosztja a vendégeket és értesít.",
        en: "Conflict? Substitute needed? AI re-assigns students and notifies them automatically.",
      },
    },
    {
      icon: "TrendingUp",
      title: { hu: "Bevétel oktatónként", en: "Revenue per instructor" },
      body: {
        hu: "Ki mennyit dolgozott, ki mennyit hozott, melyik óra a legnyereségesebb: exportálható.",
        en: "Who taught how much, who brought in what, which class is most profitable: exportable.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: { hu: "Órák, bérletek, várólista", en: "Classes, passes, waitlist" },
      body: {
        hu: "Létszámkorlát, várólista, bérletvonás, csere, online + helyszíni fizetés: minden automata.",
        en: "Capacity limits, waitlist, pass deduction, swaps, online + in-person payments: all automated.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI tartalom és social", en: "AI content and social" },
      body: {
        hu: "Heti Instagram- és Facebook-poszt-terv az óráidról, workshop-plakát, blog- és SEO-cikk a Google-nek: az AI megírja, te jóváhagyod.",
        en: "Weekly Instagram and Facebook post plan for your classes, workshop poster, blog and SEO articles for Google: AI writes, you approve.",
      },
    },
    {
      icon: "Mail",
      title: { hu: "CRM és e-mail automatizáció", en: "CRM and email automation" },
      body: {
        hu: "Minden vendégnek profil, előzmények, jegyzet. Lejáró bérlet, inaktív tag, próbaóra utáni után-követés: az AI küldi az e-mailt és a WhatsApp-üzenetet a nevedben.",
        en: "Every student gets a profile, history, notes. Expiring passes, inactive members, post-trial follow-ups: AI sends emails and WhatsApp messages on your behalf.",
      },
    },
    {
      icon: "BarChart3",
      title: { hu: "Bevétel- és óra-analitika", en: "Revenue and class analytics" },
      body: {
        hu: "Melyik óra telik, melyik vergődik, honnan jönnek az új tagok, mennyit hozott a hónap: heti riport chatben, döntési javaslatokkal.",
        en: "Which classes fill, which struggle, where new members come from, monthly revenue: weekly chat report with decision-ready suggestions.",
      },
    },
  ],

  aiScope: [
    {
      categoryKey: "content",
      examples: [
        { hu: "Heti Instagram-grid a reggeli vinyasáról és az esti restoratív óráról", en: "Weekly Instagram grid for morning vinyasa and evening restorative" },
        { hu: "Reel-storyboard 30 másodperces flow-hoz, zene- és hashtag-javaslattal", en: "Reel storyboard for a 30-second flow with music and hashtag ideas" },
        { hu: "Workshop-plakát és bevezető poszt egy klikkre", en: "Workshop poster and intro post in one click" },
      ],
    },
    {
      categoryKey: "seo",
      examples: [
        { hu: "Blogcikkek: 'Hatha jóga kezdőknek Budapesten' — a Google helyi keresésre", en: "Blog articles: 'Hatha yoga for beginners in Budapest' — local Google search" },
        { hu: "Külön oldal minden óratípusnak (vinyasa, jin, hold, prenatal)", en: "A dedicated page for each class type (vinyasa, yin, moon, prenatal)" },
        { hu: "Oktatói bio-oldal Google-barát strukturált adatokkal", en: "Instructor bio page with Google-friendly structured data" },
      ],
    },
    {
      categoryKey: "email",
      examples: [
        { hu: "Lejáró bérlet: 3 nappal előtte automata figyelmeztetés", en: "Expiring pass: automatic reminder 3 days before" },
        { hu: "'Hiányzol': 21 nap inaktivitás után baráti visszahívó üzenet", en: "'We miss you': friendly win-back after 21 days of inactivity" },
        { hu: "Új workshop bejelentés a leginkább érintett tagoknak", en: "New workshop announcement to the most relevant members" },
      ],
    },
    {
      categoryKey: "crm",
      examples: [
        { hu: "Vendégprofil: bérlet, korábbi órák, kedvenc oktató, egészségügyi jegyzet", en: "Student profile: pass, past classes, favourite teacher, health notes" },
        { hu: "Elmaradt óra automatikusan bekerül az előzményekbe", en: "Missed classes auto-logged in history" },
        { hu: "Születésnap és évforduló figyelmeztetés személyes üzenethez", en: "Birthday and anniversary alerts for a personal message" },
      ],
    },
  ],



  objections: [
    {
      question: {
        hu: "A vendégeim nem fognak online foglalni",
        en: "My students are older, they won't book online",
      },
      answer: {
        hu: "A vendégeid 70%-a már most mobiltelefonon foglal: csak más, körülményes felületeken teszi ezt. A Bespire-ben mindenkinek van saját profilja, ahol látja a bérletét, korábbi óráit és foglalásait. Egyszerűbb, mint amit eddig használtak.",
        en: "70% of your clients already book on mobile: just on clunkier interfaces. In Bespire everyone has a profile showing their pass, past classes and bookings. Simpler than whatever they're using now.",
      },
    },
    {
      question: {
        hu: "Több oktatós stúdiónk van: hogy kezeli a csapatot?",
        en: "We're a multi-instructor studio: how does it handle the team?",
      },
      answer: {
        hu: "Minden oktató saját Bespire-fiókkal dolgozik a stúdiódban. Saját naptár, saját ügyfélbázis, saját statisztika. Te tulajdonosként mindent látsz, ők csak a sajátjukat. Korlátlan oktatót adhatsz hozzá, az ár nem változik.",
        en: "Each instructor works in their own Bespire account within your studio. Own calendar, own client base, own stats. As owner you see everything, they see only theirs. Add unlimited instructors: the price doesn't change.",
      },
    },
    {
      question: {
        hu: "Nem vagyok jó technológiához",
        en: "I'm not good with technology",
      },
      answer: {
        hu: "Nem kell semmihez sem értened. Beszélgetsz az AI-val úgy, mintha egy emberrel beszélnél: 'Csinálj egy oldalt jógaoktatóként', 'Tegyél be egy hatha órát hétfő 7-re'. Az AI mindent elintéz.",
        en: "You don't need to know anything. Just chat with the AI like with a person: 'Build me a yoga instructor site', 'Add a hatha class Monday 7 AM'. AI handles the rest.",
      },
    },
    {
      question: {
        hu: "Mi van, ha váltani szeretnék? Megmaradnak a vendégeim?",
        en: "What if I want to switch? Will my students follow?",
      },
      answer: {
        hu: "Igen. Importálod a meglévő ügyféllistádat CSV-ből vagy a régi rendszeredből (MindBody, Calendly, Excel). Az AI segít a migrációban.",
        en: "Yes. Import your existing client list from CSV or your old system (MindBody, Calendly, Excel). AI helps with migration.",
      },
    },
  ],

  monetization: [
    {
      icon: "Calendar",
      title: { hu: "Jóga órák", en: "Yoga classes" },
      examples: [
        { hu: "Hatha jóga kezdőknek", en: "Hatha yoga for beginners" },
        { hu: "Vinyasa flow haladóknak", en: "Vinyasa flow for advanced" },
        { hu: "Yin jóga és meditáció", en: "Yin yoga and meditation" },
      ],
      priceHint: {
        hu: "Példa: 12 órás bérlet × 25 vendég × 18 000 Ft = 540 000 Ft / hó",
        en: "Example: 12-class pass × 25 students × 18,000 HUF = 540,000 HUF / month",
      },
    },
    {
      icon: "Heart",
      title: { hu: "Egyéni konzultáció", en: "1:1 consultation" },
      examples: [
        {
          hu: "Személyes gyakorlatösszeállítás",
          en: "Personal practice design",
        },
        { hu: "Légzéstanácsadás", en: "Breathwork coaching" },
        { hu: "Életmód coaching", en: "Lifestyle coaching" },
      ],
      priceHint: {
        hu: "Példa: 8 konzultáció × 15 000 Ft = 120 000 Ft / hó",
        en: "Example: 8 consultations × 15,000 HUF = 120,000 HUF / month",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "Workshopok és elvonulások", en: "Workshops and retreats" },
      examples: [
        { hu: "Hétvégi elvonulás", en: "Weekend retreat" },
        { hu: "Chakra workshop", en: "Chakra workshop" },
        { hu: "Meditációs intenzív", en: "Meditation intensive" },
      ],
      priceHint: {
        hu: "Példa: 1 elvonulás × 12 fő × 45 000 Ft = 540 000 Ft",
        en: "Example: 1 retreat × 12 people × 45,000 HUF = 540,000 HUF",
      },
    },
    {
      icon: "BookOpen",
      title: { hu: "Digitális termékek", en: "Digital products" },
      examples: [
        { hu: "Otthoni rutin PDF", en: "Home routine PDF" },
        { hu: "Meditációs hanganyag", en: "Guided meditation audio" },
        { hu: "Légzőgyakorlat útmutató", en: "Breathwork guide" },
      ],
      priceHint: {
        hu: "Passzív bevétel: ~100 000 Ft / hó (átlag)",
        en: "Passive revenue: ~100,000 HUF / month (average)",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Bérletek és tagságok", en: "Passes and memberships" },
      examples: [
        { hu: "10 alkalmas bérlet", en: "10-class pass" },
        { hu: "Havi korlátlan", en: "Monthly unlimited" },
        { hu: "VIP éves", en: "VIP annual" },
      ],
      priceHint: {
        hu: "Stúdió-szint: 5 oktató × 60 tag × 22 000 Ft = 6 600 000 Ft / hó potenciál",
        en: "Studio level: 5 instructors × 60 members × 22,000 HUF = 6,600,000 HUF / month potential",
      },
    },
  ],

  results: [
    {
      value: "+42%",
      label: { hu: "új vendég / negyedév", en: "new students / quarter" },
    },
    { value: "-68%", label: { hu: "no-show arány", en: "no-show rate" } },
    {
      value: "4.9★",
      label: { hu: "vendég-NPS pontszám", en: "student NPS score" },
    },
    {
      value: "10 perc",
      label: { hu: "első élő oldalig", en: "to first live page" },
    },
  ],
  testimonial: {
    quote: {
      hu: "A vendégek most már éjjel 11-kor foglalnak a reggeli vinyasára. Régebben WhatsAppon kergettem mindenkit: most az AI mindent intéz, én csak tartom az órát.",
      en: "Students now book the morning vinyasa at 11 PM. I used to chase everyone on WhatsApp: now AI handles it all, I just teach.",
    },
    name: "Kovács Réka",
    role: {
      hu: "Jóga oktató, stúdió-tulajdonos",
      en: "Yoga instructor, studio owner",
    },
    city: { hu: "Budapest, VII. kerület", en: "Budapest, District VII" },
    avatarInitials: "KR",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a stílusod",
        en: "Tell us your style in chat",
      },
      body: {
        hu: "Hatha, vinyasa, kismama, gyerek: az AI megérti és felépíti az óra-katalógust.",
        en: "Hatha, vinyasa, prenatal, kids: AI understands and builds your class catalogue.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti az oldalt + bérleteket",
        en: "AI builds your site + passes",
      },
      body: {
        hu: "Élő weboldal saját brandeddel, 10-es bérlet, havi korlátlan, próbaóra: minden konfigurálva.",
        en: "Live website with your branding, 10-pass, monthly unlimited, trial class: all configured.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Importáljuk a meglévő vendégeidet",
        en: "We migrate your students",
      },
      body: {
        hu: "CSV-ből, MindBody-ból, Excel-ből: kartonok, bérlet-állomány, korábbi órák.",
        en: "From CSV, MindBody, Excel: profiles, pass balances, past classes.",
      },
    },
    {
      step: 4,
      title: { hu: "Élő, a vendégek foglalnak", en: "Live, students book" },
      body: {
        hu: "QR-kód a stúdió ajtaján, link az Instagramban: a foglalások jönnek.",
        en: "QR code on the studio door, link in your Instagram bio: bookings roll in.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Apple Calendar",
    "Stripe",
    "SimplePay",
    "Apple Pay",
    "Google Pay",
    "Meta (FB/IG)",
    "WhatsApp Business",
    "Mailchimp",
  ],
  breadcrumbLabel: {
    hu: "Jóga- és Pilates-oktatók",
    en: "Yoga & Pilates instructors",
  },
  similarPersonas: [
    "fitness-personal-trainers",
    "physiotherapists",
    "therapists-coaches",
  ],
};
